import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService, UserModel } from '../../services/user.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InviteUser } from '../inviteuser/inviteuser';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { ProjectStateService } from '../../services/project-state.service';

@Component({
  selector: 'app-people',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    ToggleSwitch,
    FormsModule,
    Toast
  ],
  templateUrl: './people.html',
  styleUrl: './people.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class People implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  users: UserModel[] = [];
  filteredUsers: UserModel[] = [];
  loading = false;
  pageSize = 10;
  rowsPerPageOptions: number[] = [10];
  dialogRef: DynamicDialogRef | null = null;

  constructor(
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private messageService: MessageService,
    private projectStateService: ProjectStateService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.projectStateService.pageSearch$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(search => {
      this.applySearch(search);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applySearch(search: string): void {
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      this.filteredUsers = [...this.users];
    } else {
      this.filteredUsers = this.users.filter(u =>
        (u.firstName || '').toLowerCase().includes(term) ||
        (u.lastName || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term) ||
        (u.roleName || '').toLowerCase().includes(term) ||
        (u.userTypeName || '').toLowerCase().includes(term) ||
        String(u.userId || '').includes(term)
      );
    }
    this.updatePaginationConfig(this.filteredUsers.length);
    this.cdr.markForCheck();
  }

  loadUsers(): void {
    this.loading = true;
    this.userService.getUserList().subscribe({
      next: (users) => {
        this.users = users ?? [];
        this.applySearch(this.projectStateService.getPageSearch());
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.users = [];
        this.filteredUsers = [];
        this.updatePaginationConfig(0);
        this.loading = false;
      }
    });
  }

  private updatePaginationConfig(totalUsers: number): void {
    const maxOption = totalUsers > 0 ? Math.ceil(totalUsers / 10) * 10 : 10;
    this.rowsPerPageOptions = Array.from({ length: maxOption / 10 }, (_, index) => (index + 1) * 10);

    if (!this.rowsPerPageOptions.includes(this.pageSize)) {
      this.pageSize = 10;
    }
  }

  getUserAccentColor(user: UserModel, _rowIndex: number): string {
    return user.isActive ? '#55a55e' : '#ed1c4d';
  }

  getRoleTagClass(roleName?: string): string {
    const normalizedRole = (roleName ?? '').trim().toLowerCase();
    return normalizedRole === 'admin' ? 'admin' : '';
  }

  private maximizeInviteUserDialog(): void {
    setTimeout(() => {
      const maximizeButton = document.querySelector('.invite-user-dialog .p-dialog-maximize-button') as HTMLButtonElement | null;
      maximizeButton?.click();
    });
  }

  editUser(user: UserModel): void {
    this.dialogRef = this.dialogService.open(InviteUser, {
      header: 'Edit User',
      width: '600px',
      maximizable: true,
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog invite-user-dialog',
      contentStyle: {
        overflow: 'hidden',
        padding: '0'
      },
      data: {
        projectId: 0,
        isEditMode: true,
        user
      }
    });

    this.maximizeInviteUserDialog();

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result && result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User updated successfully'
          });
          this.loadUsers();
        }
      }
    });
  }

  deleteUser(user: UserModel): void {
    console.log('Delete user:', user);
    // TODO: Implement delete functionality
  }

  handleStatusToggle(user: UserModel): void {
    if (!user.userId) {
      return;
    }

    const newStatus = user.isActive;
    const statusText = newStatus ? 'activated' : 'deactivated';

    this.userService.toggleUserStatus(user.userId, newStatus).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `User ${statusText} successfully`
        });
        this.cdr.markForCheck();
      },
      error: (error) => {
        user.isActive = !newStatus;
        this.cdr.markForCheck();
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Failed to update user status'
        });
      }
    });
  }

  addNewUser(): void {
    this.dialogRef = this.dialogService.open(InviteUser, {
      header: 'Add User',
      width: '600px',
      maximizable: true,
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog invite-user-dialog',
      contentStyle: {
        overflow: 'hidden',
        padding: '0'
      },
      data: {
        projectId: 0
      }
    });

    this.maximizeInviteUserDialog();

    this.dialogRef?.onClose.subscribe({
      next: (result) => {
        if (result && result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'User invitation sent successfully'
          });
          this.loadUsers();
        }
      }
    });
  }
}

